/** The dashboard side of the calculator's "Sample home" demo (apps/solar-frontend/js/api.js, DemoBridge).
 *
 * The demo page (`/api/public/embed-demo`) holds no key or data: it posts every public-API request it would make to
 * this window, which answers from the signed-in user's sample-home endpoints (`/v1/orgs/{org}/widget-demo…`). Only
 * the recorded samples exist there, so whatever the frame asks, the answer is a sample: address suggestions are the
 * samples (whatever is typed, named by their labels; the API never gives their real address or location), the design
 * is "ready" at once, pricing goes through the real quote engine with the company's current settings, and nothing is
 * stored (the contact details never leave the frame). The commitment answers with a sample
 * quote number and send nothing. Messages are accepted only from that iframe's window and origin.
 *
 * The Branding page edits the brand before saving it: `setBrand` makes `/config` answer with that draft and sends it
 * to the frame (`{type: 'solarDemoBrand', brand}`, only to the frame's origin), which re-applies it in place. */
import { api, ApiError, rawFetch, unwrap, type S } from "../api/client";

type Body = Record<string, unknown> | undefined;
interface DemoRequest {
  type: "solarDemoRequest";
  id: number;
  method: string;
  path: string;
  body?: Body;
}
interface Reply {
  status: number;
  data?: unknown;
  error?: string;
  code?: string;
}
interface Visit {
  roof_type?: string;
  electrical_usage?: S["PublicElectricalUsage"];
  selection: S["WidgetDemoSelection"];
}

const SIZING = ["selected_panels", "offset_pct", "selected_panel_count"] as const;

function fail(status: number, error: string, code = "invalid_request"): Reply {
  return { status, error, code };
}

function nearest(samples: S["WidgetDemoSample"][], lat: number, lng: number): S["WidgetDemoSample"] | undefined {
  let best: S["WidgetDemoSample"] | undefined;
  let bestD = Infinity;
  for (const s of samples) {
    const d = (s.location.lat - lat) ** 2 + (s.location.lng - lng) ** 2;
    if (d < bestD) [best, bestD] = [s, d];
  }
  return best;
}

/** Applies a calculator recalculation (as `/public/quotes/{id}/recalculate` would) to the visit's selection. */
export function applyRecalc(selection: S["WidgetDemoSelection"], body: Record<string, unknown>): S["WidgetDemoSelection"] {
  const next: Record<string, unknown> = { ...selection };
  const clearSizing = () => SIZING.forEach((k) => delete next[k]);
  if (Array.isArray(body.selected_panels) && body.selected_panels.length) {
    clearSizing();
    next.selected_panels = body.selected_panels;
  }
  for (const name of ["offset_pct", "selected_panel_count"] as const) {
    if (typeof body[name] === "number") {
      clearSizing();
      next[name] = body[name];
    }
  }
  if (typeof body.financing === "string") next.financing = body.financing;
  if (typeof body.battery_option_id === "string" && body.battery_option_id) next.battery_option_id = body.battery_option_id;
  else if (body.include_battery === false) delete next.battery_option_id;
  return next as S["WidgetDemoSelection"];
}

export class DemoBridge {
  private visits = new Map<string, Visit>();
  private brand: S["BrandSettings"] | null = null;
  private target: { frame: () => HTMLIFrameElement | null; origin: string } | null = null;

  constructor(
    private orgId: string,
    private demo: S["WidgetDemo"],
  ) {}

  private sample(id: string): S["WidgetDemoSample"] | undefined {
    return this.demo.samples.find((s) => s.id === id);
  }

  private ready(id: string) {
    return { success: true, quote_id: id, status: "ready", progress: 1, current_stage: "quote", message: "Sample home ready." };
  }

  private async price(id: string): Promise<Reply> {
    const visit = this.visits.get(id) ?? { selection: {} };
    const data = await unwrap(
      api.POST("/v1/orgs/{org_id}/widget-demo/{sample_id}/quote", {
        params: { path: { org_id: this.orgId, sample_id: id } },
        body: { roof_type: visit.roof_type, electrical_usage: visit.electrical_usage, selection: visit.selection },
      }),
    );
    return { status: 200, data };
  }

  private async image(id: string): Promise<Reply> {
    if (!this.sample(id)) return fail(404, "Unknown sample", "not_found");
    const response = await rawFetch(`/api/v1/orgs/${this.orgId}/widget-demo/${encodeURIComponent(id)}/image`);
    return { status: 200, data: await response.blob() };
  }

  /** A sample commitment ("SQ-<year>-000000", valid for the brand's days); nothing is stored or sent. */
  private offer(body: Body = {}): Reply {
    const contact = (body.contact ?? null) as S["QuoteContact"] | null;
    const brand = this.brand ?? this.demo.config.brand;
    const after = brand.after_quote;
    const days = after?.validity_days ?? 30;
    const valid = new Date(Date.now() + days * 86400000).toISOString().slice(0, 10);
    const offer: S["QuoteOffer"] = {
      quote_number: `SQ-${new Date().getFullYear()}-000000`,
      status: body.action === "booking_opened" ? "booking_opened" : "requested",
      expired: false,
      valid_until: valid,
      committed_at: new Date().toISOString(),
      action: body.action === "booking_opened" ? "booking_opened" : "requested",
      contact,
      revision_count: 1,
      link_url: null,
      link_token: null,
      link_last4: null,
    };
    // the company's own next steps; ours (null) the calculator shows in the visitor's language
    const nextSteps = after?.next_steps ? after.next_steps.split("{company}").join(brand.company_name) : null;
    return { status: 200, data: { success: true, offer, email_sent_to: null, next_steps: nextSteps } };
  }

  /** The answer to one request of the demo calculator. */
  async answer(method: string, path: string, body: Body = {}): Promise<Reply> {
    const url = new URL(path, "https://calculator.invalid/");
    const p = url.pathname;
    try {
      if (method === "GET" && p === "/config") {
        const config = { ...this.demo.config, brand: this.brand ?? this.demo.config.brand };
        return { status: 200, data: config };
      }
      if (method === "GET" && p === "/places/autocomplete") {
        const suggestions = this.demo.samples.map((s) => ({ place_id: s.id, description: s.label, main_text: s.label, secondary_text: s.description }));
        return { status: 200, data: { success: true, suggestions } };
      }
      const place = p.match(/^\/places\/([\w-]+)$/);
      if (method === "GET" && place) {
        const s = this.sample(place[1]!);
        if (!s) return fail(404, "Unknown sample home", "not_found");
        return { status: 200, data: { success: true, formatted_address: s.label, location: s.location } };
      }
      if (method === "POST" && p === "/validate-address") {
        return fail(400, "In this preview, choose one of the sample homes from the suggestions.");
      }
      const image = p.match(/^\/widget-demo\/([\w-]+)\/image$/);
      if (method === "GET" && image) return await this.image(image[1]!);
      if (method === "GET" && p === "/site-map") {
        const s = nearest(this.demo.samples, Number(url.searchParams.get("lat")), Number(url.searchParams.get("lng")));
        if (!s) return fail(404, "No sample home", "not_found");
        return { status: 200, data: { success: true, image_url: `widget-demo/${s.id}/image`, scale: 1, tile_size: 256, attribution: "", ...s.map } };
      }
      if (method === "POST" && p === "/quotes") {
        const s = nearest(this.demo.samples, Number(body.lat), Number(body.lng));
        if (!s) return fail(404, "No sample home", "not_found");
        this.visits.set(s.id, { roof_type: typeof body.roof_type === "string" ? body.roof_type : undefined, selection: {} });
        return { status: 200, data: this.ready(s.id) };
      }
      // After the quote: the commitment looks real in the preview but nothing is created or sent (the visitor's
      // details never leave the frame).
      const offer = p.match(/^\/quotes\/([\w-]+)\/commit$/);
      if (method === "POST" && offer && this.sample(offer[1]!)) return this.offer(body);
      // The sample homes have no utility registry region: only "not listed" is offered.
      if (/^\/quotes\/[\w-]+\/utilities$/.test(p)) return { status: 200, data: { items: [] } };
      const quote = p.match(/^\/quotes\/([\w-]+)\/(status|generate|details|recalculate|image)$/);
      if (quote && this.sample(quote[1]!)) {
        const [, id, action] = quote as unknown as [string, string, string];
        const visit = this.visits.get(id) ?? { selection: {} };
        this.visits.set(id, visit);
        if (action === "status") return { status: 200, data: this.ready(id) };
        if (action === "image") return await this.image(id);
        if (action === "generate") {
          // personal_info is ignored on purpose: nothing about the visitor leaves the dashboard.
          if (body.electrical_usage && typeof body.electrical_usage === "object") visit.electrical_usage = body.electrical_usage as S["PublicElectricalUsage"];
          if (typeof body.roof_type === "string") visit.roof_type = body.roof_type;
        }
        if (action === "recalculate") visit.selection = applyRecalc(visit.selection, body);
        return await this.price(id);
      }
      return fail(400, "This is not available in the sample-home preview.");
    } catch (e) {
      if (e instanceof ApiError) return fail(e.status >= 500 ? 500 : 400, e.message);
      return fail(0, e instanceof Error ? e.message : String(e), "network");
    }
  }

  /** Shows `brand` (unsaved) in the frame now, and in its answers to `/config` from now on. */
  setBrand(brand: S["BrandSettings"] | null): void {
    this.brand = brand;
    const target = this.target?.frame()?.contentWindow;
    if (brand && target && this.target) target.postMessage({ type: "solarDemoBrand", brand }, this.target.origin);
  }

  /** Listens for the demo frame's requests (and Esc pressed inside it); returns the unsubscribe function. */
  listen(frame: () => HTMLIFrameElement | null, frameOrigin: string, onEscape?: () => void): () => void {
    this.target = { frame, origin: frameOrigin };
    const handler = (event: MessageEvent) => {
      const target = frame()?.contentWindow;
      const msg = event.data as DemoRequest | { type: "solarDemoEscape" };
      if (!target || event.source !== target || event.origin !== frameOrigin || !msg) return;
      if (msg.type === "solarDemoEscape") return onEscape?.();
      if (msg.type !== "solarDemoRequest") return;
      void this.answer(String(msg.method), String(msg.path), (msg.body as Body) ?? {}).then((reply) => {
        target.postMessage({ type: "solarDemoResponse", id: msg.id, ...reply }, frameOrigin);
      });
    };
    window.addEventListener("message", handler);
    return () => {
      window.removeEventListener("message", handler);
      this.target = null;
    };
  }
}
